/**
 * src/i18n/index.ts — Bootstrap i18n web V2 (DealSpot Phase 3).
 *
 * Stack: i18next + react-i18next + i18next-browser-languagedetector.
 * Resources: import estático (sem HTTP backend); bundles em build time.
 *
 * Idiomas declarados (6): en (default+fallback), pt-BR, de, es, fr, it.
 * Namespaces (8): common, landing, auth, business, profile, privacy, admin, legal.
 * Note: `legal` namespace adicionado retroativamente no Plan 03-02b (round-2 revision)
 * — Plans 09/12 consumiam t('legal.*') antes do namespace existir.
 *
 * V1 (src/infrastructure/i18n/i18n.config.ts + public/locales/) fica órfão
 * por decisão 2026-05-12 — deleta na Phase 16 Cleanup.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import iso from 'i18n-iso-countries';

// i18n-iso-countries locales (6) — alimenta useCountryOptions.
import isoEn from 'i18n-iso-countries/langs/en.json';
import isoPt from 'i18n-iso-countries/langs/pt.json';
import isoDe from 'i18n-iso-countries/langs/de.json';
import isoEs from 'i18n-iso-countries/langs/es.json';
import isoFr from 'i18n-iso-countries/langs/fr.json';
import isoIt from 'i18n-iso-countries/langs/it.json';

iso.registerLocale(isoEn);
iso.registerLocale(isoPt);
iso.registerLocale(isoDe);
iso.registerLocale(isoEs);
iso.registerLocale(isoFr);
iso.registerLocale(isoIt);

// Translation resources — 8 namespaces × 6 idiomas (48 imports).
import enCommon from './locales/en/common.json';
import enLanding from './locales/en/landing.json';
import enAuth from './locales/en/auth.json';
import enBusiness from './locales/en/business.json';
import enProfile from './locales/en/profile.json';
import enPrivacy from './locales/en/privacy.json';
import enAdmin from './locales/en/admin.json';
import enLegal from './locales/en/legal.json';

import ptBRCommon from './locales/pt-BR/common.json';
import ptBRLanding from './locales/pt-BR/landing.json';
import ptBRAuth from './locales/pt-BR/auth.json';
import ptBRBusiness from './locales/pt-BR/business.json';
import ptBRProfile from './locales/pt-BR/profile.json';
import ptBRPrivacy from './locales/pt-BR/privacy.json';
import ptBRAdmin from './locales/pt-BR/admin.json';
import ptBRLegal from './locales/pt-BR/legal.json';

import deCommon from './locales/de/common.json';
import deLanding from './locales/de/landing.json';
import deAuth from './locales/de/auth.json';
import deBusiness from './locales/de/business.json';
import deProfile from './locales/de/profile.json';
import dePrivacy from './locales/de/privacy.json';
import deAdmin from './locales/de/admin.json';
import deLegal from './locales/de/legal.json';

import esCommon from './locales/es/common.json';
import esLanding from './locales/es/landing.json';
import esAuth from './locales/es/auth.json';
import esBusiness from './locales/es/business.json';
import esProfile from './locales/es/profile.json';
import esPrivacy from './locales/es/privacy.json';
import esAdmin from './locales/es/admin.json';
import esLegal from './locales/es/legal.json';

import frCommon from './locales/fr/common.json';
import frLanding from './locales/fr/landing.json';
import frAuth from './locales/fr/auth.json';
import frBusiness from './locales/fr/business.json';
import frProfile from './locales/fr/profile.json';
import frPrivacy from './locales/fr/privacy.json';
import frAdmin from './locales/fr/admin.json';
import frLegal from './locales/fr/legal.json';

import itCommon from './locales/it/common.json';
import itLanding from './locales/it/landing.json';
import itAuth from './locales/it/auth.json';
import itBusiness from './locales/it/business.json';
import itProfile from './locales/it/profile.json';
import itPrivacy from './locales/it/privacy.json';
import itAdmin from './locales/it/admin.json';
import itLegal from './locales/it/legal.json';

export const SUPPORTED_LANGUAGES = ['en', 'pt-BR', 'de', 'es', 'fr', 'it'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const I18N_NAMESPACES = [
  'common',
  'landing',
  'auth',
  'business',
  'profile',
  'privacy',
  'admin',
  'legal',
] as const;
export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        landing: enLanding,
        auth: enAuth,
        business: enBusiness,
        profile: enProfile,
        privacy: enPrivacy,
        admin: enAdmin,
        legal: enLegal,
      },
      'pt-BR': {
        common: ptBRCommon,
        landing: ptBRLanding,
        auth: ptBRAuth,
        business: ptBRBusiness,
        profile: ptBRProfile,
        privacy: ptBRPrivacy,
        admin: ptBRAdmin,
        legal: ptBRLegal,
      },
      de: {
        common: deCommon,
        landing: deLanding,
        auth: deAuth,
        business: deBusiness,
        profile: deProfile,
        privacy: dePrivacy,
        admin: deAdmin,
        legal: deLegal,
      },
      es: {
        common: esCommon,
        landing: esLanding,
        auth: esAuth,
        business: esBusiness,
        profile: esProfile,
        privacy: esPrivacy,
        admin: esAdmin,
        legal: esLegal,
      },
      fr: {
        common: frCommon,
        landing: frLanding,
        auth: frAuth,
        business: frBusiness,
        profile: frProfile,
        privacy: frPrivacy,
        admin: frAdmin,
        legal: frLegal,
      },
      it: {
        common: itCommon,
        landing: itLanding,
        auth: itAuth,
        business: itBusiness,
        profile: itProfile,
        privacy: itPrivacy,
        admin: itAdmin,
        legal: itLegal,
      },
    },
    lng: undefined, // deixa o detector decidir; fallback rola se idioma não suportado
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    ns: [...I18N_NAMESPACES],
    defaultNS: 'common',
    // Pattern Phase 8: callers escrevem t('landing.hero.headline') com '.' como
    // namespace separator (em vez do default ':'). i18next splita na primeira
    // ocorrência como namespace + resto como key path. Zero colisão verificada
    // entre nomes de namespace e top-level keys (ver memory project_i18n_pattern).
    nsSeparator: '.',
    interpolation: {
      escapeValue: false, // React já escapa
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    returnNull: false,
  });

export default i18n;
